import React, { useEffect, useState } from "react";
import { adminAxios, mediaUrl } from "../api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import ImageField from "../components/ImageField";

const blank = {
  name: "", short_description: "", full_description: "", images: [], video_url: "",
  category: "", sku: "", price: 0, discount: 0, tax_percent: 3, stock: 0,
  featured: false, active: true, published: true, variants: [], specifications: [],
  benefits: [], seo_title: "", seo_description: "", seo_keywords: "", slug: "", display_order: 0,
};

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const ProductsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await adminAxios.get("/products")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...blank, ...p,
      images: p.images || [], benefits: p.benefits || [], specifications: p.specifications || [], variants: p.variants || [] });
    setEditing(p.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), discount: Number(form.discount), tax_percent: Number(form.tax_percent), stock: Number(form.stock), display_order: Number(form.display_order) };
      if (editing === "new") await adminAxios.post("/products", payload);
      else await adminAxios.put(`/products/${editing}`, payload);
      toast.success("Product saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    await adminAxios.patch(`/products/${p.id}/publish`, { published: !p.published });
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await adminAxios.delete(`/products/${p.id}`); toast.success("Deleted"); load();
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addVariant = () =>
    setForm({ ...form, variants: [...(form.variants || []), { id: "v-" + Math.random().toString(36).slice(2, 8), label: "", price: 0, stock: 0, active: true, perks: [] }] });
  const removeVariant = (idx) =>
    setForm({ ...form, variants: (form.variants || []).filter((_, i) => i !== idx) });
  const setVField = (idx, key, value) => {
    const list = [...(form.variants || [])];
    list[idx] = { ...list[idx], [key]: value };
    setForm({ ...form, variants: list });
  };
  const setV = (idx, key, isNum = false) => (e) => setVField(idx, key, isNum ? Number(e.target.value) : e.target.value);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-semibold text-slate-900">Products</h1><p className="text-slate-500">Manage catalogue shown on the website</p></div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" /> New Product</Button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr><th className="px-4 py-3 font-medium">Product</th><th className="px-4 py-3 font-medium">Price</th><th className="px-4 py-3 font-medium">Stock</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3"><div className="flex items-center gap-3"><img src={(p.images||[])[0]} alt="" className="w-10 h-10 rounded object-cover bg-slate-100" /><div><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-400">{p.slug}</div></div></div></td>
                  <td className="px-4 py-3">₹{Number(p.final_price ?? p.price).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">{p.stock}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{p.published ? "Published" : "Draft"}</span></td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                    <button onClick={() => togglePublish(p)} title="Publish/Unpublish" className="p-2 rounded hover:bg-slate-100 text-slate-500">{p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    <button onClick={() => openEdit(p)} className="p-2 rounded hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p)} className="p-2 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setEditing(null)}>
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-semibold text-lg">{editing === "new" ? "New Product" : "Edit Product"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <Row label="Name"><Input value={form.name} onChange={setF("name")} /></Row>
              <Row label="Short Description"><Input value={form.short_description} onChange={setF("short_description")} /></Row>
              <Row label="Full Description"><Textarea rows={3} value={form.full_description} onChange={setF("full_description")} /></Row>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Category"><Input value={form.category} onChange={setF("category")} /></Row>
                <Row label="SKU"><Input value={form.sku} onChange={setF("sku")} /></Row>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Row label="Price"><Input type="number" value={form.price} onChange={setF("price")} /></Row>
                <Row label="Discount"><Input type="number" value={form.discount} onChange={setF("discount")} /></Row>
                <Row label="GST %"><Input type="number" value={form.tax_percent} onChange={setF("tax_percent")} /></Row>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Stock"><Input type="number" value={form.stock} onChange={setF("stock")} /></Row>
                <Row label="Display Order"><Input type="number" value={form.display_order} onChange={setF("display_order")} /></Row>
              </div>
              <div>
                <Label className="text-slate-600 text-sm mb-1.5 block">Product Images</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(form.images || []).map((img, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-lg border border-slate-200 overflow-hidden group">
                      <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, x) => x !== i) })}
                        className="absolute top-0 right-0 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <ImageField value="" onChange={(url) => url && setForm({ ...form, images: [...(form.images || []), url] })} label="Upload / add an image" compact />
              </div>
              <Row label="Benefits (one per line)"><Textarea rows={3} value={toLines(form.benefits)} onChange={(e) => setForm({ ...form, benefits: fromLines(e.target.value) })} /></Row>
              <Row label="Slug (optional)"><Input value={form.slug} onChange={setF("slug")} /></Row>
              <div className="grid grid-cols-1 gap-3">
                <Row label="SEO Title"><Input value={form.seo_title} onChange={setF("seo_title")} /></Row>
                <Row label="SEO Description"><Textarea rows={2} value={form.seo_description} onChange={setF("seo_description")} /></Row>
                <Row label="SEO Keywords"><Input value={form.seo_keywords} onChange={setF("seo_keywords")} /></Row>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-700 text-sm font-semibold">Variants (shown on checkout)</Label>
                  <button type="button" onClick={addVariant} className="text-xs font-medium text-slate-900 border border-slate-300 rounded-md px-2 py-1 hover:bg-slate-50">+ Add Variant</button>
                </div>
                <div className="space-y-3">
                  {(form.variants || []).map((v, idx) => (
                    <div key={idx} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-500">Variant {idx + 1}</span>
                        <button type="button" onClick={() => removeVariant(idx)} className="text-red-500 text-xs hover:underline">Remove</button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={v.label || ""} onChange={setV(idx, "label")} placeholder="Name (e.g. 8 Ct Silver Locket)" className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.price ?? ""} onChange={setV(idx, "price", true)} type="number" placeholder="Price" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.stock ?? ""} onChange={setV(idx, "stock", true)} type="number" placeholder="Stock" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.carat || ""} onChange={setV(idx, "carat")} placeholder="Carat" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.metal || ""} onChange={setV(idx, "metal")} placeholder="Metal/Material" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.weight || ""} onChange={setV(idx, "weight")} placeholder="Weight" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.gemstone || ""} onChange={setV(idx, "gemstone")} placeholder="Gemstone" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.badge || ""} onChange={setV(idx, "badge")} placeholder="Badge (optional)" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <input value={v.sku || ""} onChange={setV(idx, "sku")} placeholder="SKU" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                        <div className="col-span-2"><ImageField value={v.image} onChange={(url) => setVField(idx, "image", url)} label="Variant image" compact /></div>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={v.active !== false} onChange={(e) => setVField(idx, "active", e.target.checked)} /> Active</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!v.highlight} onChange={(e) => setVField(idx, "highlight", e.target.checked)} /> Highlight</label>
                      </div>
                    </div>
                  ))}
                  {(form.variants || []).length === 0 && <p className="text-xs text-slate-400">No variants. The base product price will be used. Add variants to offer choices on checkout.</p>}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div><Label className="text-slate-600 text-sm mb-1.5 block">{label}</Label>{children}</div>
);

export default ProductsAdmin;
